import {
  ElasticSearchService,
  ESExtras,
  ESSort,
} from '../api-server/elastic-search.service';
import {
  collection,
  collectionData,
  CollectionReference,
  deleteDoc,
  doc,
  docData,
  DocumentData,
  DocumentReference,
  Firestore,
  FirestoreDataConverter,
  increment,
  limit,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  serverTimestamp,
  setDoc,
  SnapshotOptions,
  updateDoc,
  where,
  WithFieldValue,
} from '@angular/fire/firestore';
import { map, Observable, take, throttleTime } from 'rxjs';
import { toDate } from '../helpers/utils';
import { get, set, update } from 'lodash-es';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
// import { BaseModel } from '@shared/models/common.model';
import { endOfDay, startOfDay } from 'date-fns';
import { BaseModel } from '../models/common.model';

/**
 * Clase base para ser reutilizada en los db-services
 *  super(firestore, es, 'orders', esExtra, esSource)
 *
 * Se pueden customizar los serializadores.
 * 1.- Es se declara nuevamente esNormalize
 * 2.- Para ffirestore tenemos dosfunciones
 * this.genericConverter.toFirestore =  (data: WithFieldValue<Model>) => {....}
 * this.genericConverter.fromFirestore =  ( snapshot: QueryDocumentSnapshot, options: SnapshotOptions) => {....}
 *
 */
export interface EsFirestoreBaseOptions {
  utcDateProperties?: Array<string>;
}

export interface upsertOptions {
  isNew?: boolean;
  merge?: boolean;
}


export abstract class EsFirestoreBase<Model extends BaseModel> {
  public collection: CollectionReference<any>;
  // public dateProperties = ['createdAt', 'updatedAt']
  // private options;

  esNormalize = (item: any) => {
    if (this.options.utcDateProperties) {
      this.options.utcDateProperties.forEach((property) =>
        setUtcEsPropertyDate(item, property)
      );
    }
    this.dateProperties.forEach((property) =>
      setEsPropertyDate(item, property)
    );
    return item as Model;
  };

  genericConverter:FirestoreDataConverter<any>

  constructor(
    protected firestore: Firestore,
    protected es: ElasticSearchService,
    protected collectionName: string,
    protected esExtras: ESExtras = null,
    protected esSource: Array<string> = null,
    protected dateProperties = ['createdAt', 'updatedAt'],
    protected options: EsFirestoreBaseOptions = {}
  ) {
    const utcDateProperties = this.options.utcDateProperties ?? null;
    const _dateProperties = this.dateProperties;
    if (this.firestore) {
      //Converter
      this.genericConverter = {
        // dateProperties: this.dateProperties,

        toFirestore(data: WithFieldValue<Model>): DocumentData {
          if (utcDateProperties) {
            utcDateProperties.forEach((property) =>
              getPropertyUtcDate(data, property)
            );
          }
          return data as DocumentData;
        },
        fromFirestore(
          snapshot: QueryDocumentSnapshot,
          options: SnapshotOptions
        ): Model {
          const data = snapshot.data(options)!;
          if (utcDateProperties) {
            utcDateProperties.forEach((property) =>
              setPropertyUtcDate(data, property)
            );
          }
          _dateProperties.forEach((property) =>
            setPropertyDate(data, property)
          );
          return data as Model;
        },
      };
      //Collection
      this.collection = collection(
        this.firestore,
        this.collectionName
      ).withConverter(this.genericConverter);
    }
  }
  get$(id: string): Observable<Model> {
    const docRef = doc(this.collection, id);
    return docData(docRef, { idField: 'id' }) as Observable<Model>;
  }

  getAll$(): Observable<Model[]> {
    const _query = query(this.collection, limit(100)); // TODO: handle this limit properly
    return collectionData(_query, { idField: 'id' }) as Observable<Model[]>;
  }
  
  findAllBy$(options): Observable<Model[]> {
    let _where: QueryConstraint[] = [];
    for (const key in options) {
      _where.push(where(key, '==', options[key]));
    }
    const _query = query(this.collection, ..._where);
    return collectionData(_query, { idField: 'id' }).pipe(
      map(docs => docs?.length ? docs : [])
    )
  }  

  upsert(_data, options: upsertOptions = { isNew: false, merge: true }): Promise<any> {
    const { ...data } = _data;
    let { isNew, merge } = options;


    data.updatedAt = serverTimestamp();
    data._version = increment(1);
    if (!data.id) {
      isNew = true;
      data.id = this.createId();
    }
    if (isNew) {
      data.createdAt = serverTimestamp();
    }
    merge = !isNew && merge;
    const docRef: DocumentReference = doc(this.collection, data.id);
    return !isNew && !merge
      ? updateDoc(docRef, data)
      : setDoc(docRef, data, { merge }).then(() => data.id);
  }

  delete(id): Promise<any> {
    const docRef = doc(this.collection, id);
    return deleteDoc(docRef);
  }

  onChange(): Observable<any> {
    const docRef = doc(this.firestore, 'refresh', this.collectionName);
    return docData(docRef).pipe(throttleTime(2000, undefined, { leading: true, trailing: true })) as Observable<any> ;
  }

  createId() {
    return doc(this.collection).id;
  }

  query$(paginator, sort, filter, options?): Observable<any> {
    const extras = this.esExtras;
    const source = this.esSource;
    const index = this.collectionName;
    const body = this.es.mtToBody(paginator, sort, filter, extras, source);
    return this.es.query$(index, body, this.esNormalize).pipe(take(1));
  }
}

export const mapPrimeTable2Mat = (conditions) => {
  let sort: ESSort | ESSort[] = { active: null, direction: null };
  const { options } = conditions;
  //Check Sort
  if (options.multiSortMeta?.length) {
    const mSort = [] as ESSort[];
    options.multiSortMeta.forEach(s => {
      if (s.field) {
        mSort.push({
          active: s.field,
          direction: s.order == 1 ? 'asc' : s.order == -1 ? 'desc' : null
        })
      }
    });
    sort = mSort;
  } else if (options.sortField) {
    sort.active = options.sortField;
    sort.direction =
      options.sortOrder == 1 ? 'asc' : options.sortOrder == -1 ? 'desc' : null;
  }

  //Check Filter
  const filters = ngFilterToMatFilter(options.filters);
  //name: [{value: '01', matchMode: 'contains', operator: 'and']
  //const filters = {name: [{ key: 'name', value: name, op: '==' }]};

  //Check Paginator
  const paginator = ngPaginatorToMatPaginator(options);
  return { paginator, sort, filters } as any;
};



const ngPaginatorToMatPaginator = (options) => {
  return { pageIndex: options.first / options.rows, pageSize: options.rows };
};

const ngFilterToMatFilter = (ngFilter) => {
  const filters = {};
  if (ngFilter) {
    Object.keys(ngFilter).forEach((property) => {
      const filter: any = ngFilter[property];
      if (filter.length) {
        filters[property] = filter
          .filter((t) => t.value)
          .map((f) => {
            //TODO Ampliar con las demás opciones de filter
            let ret;
            if (f.matchMode === 'dateIs') {
              f.value.fi
              f.value.fe;
              const value = {
                fi: startOfDay(f.value),
                fe: endOfDay(f.value)
              }
              ret = { key: property, value, op: 'range' };
            } else {
              ret = { key: property, value: f.value, op: '==' };

            }

            return ret;
          })
      }
    });
  }
  return filters;
};

export const setPropertyDate = (data, key) => {
  const value = get(data, key, null);
  if (value) {
    set(data, key, toDate(value));
  }
};

const setEsPropertyDate = (data, key) => {
  const value = get(data, key, null);
  if (value) {
    set(data, key, new Date(value['_seconds'] * 1000));
  }
};

export const getPropertyUtcDate = (data, key) => {
  const value = get(data, key, null);
  if (value) {
    // set(data, key, zonedTimeToUtc(value, 'UTC'));
    set(data, key, fromZonedTime(value, 'UTC'));
  }
};
export const setPropertyUtcDate = (data, key) => {
  const value = get(data, key, null);
  if (value) {
    set(data, key, toZonedTime(toDate(value), 'UTC'));
  }
};
export const setUtcEsPropertyDate = (data, key) => {
  const value = get(data, key, null);
  if (value) {
    set(data, key, toZonedTime(new Date(value['_seconds'] * 1000), 'UTC'));
  }
};
